import { COGS_CATEGORIES, formatJMD, parseEntryDate, sumJmd } from './finances';
import { effectiveRange, filterByPeriod, monthsBetween } from './period';

// Report shapes live here rather than inside the components so the on-screen
// table and the CSV export are always built from the same numbers — the
// classic way these drift is a component computing a total inline that the
// export then recomputes slightly differently.

// The P&L reads as sales lines, not as the raw `channel` enum stored on the
// entry. Anything not mapped here falls through to its own name, so adding a
// channel shows up correctly rather than silently merging into another line.
export const REVENUE_LINE_LABELS = {
  'In-Person': 'Sale',
  Website: 'Website Sale',
  Wholesale: 'Wholesale',
};

// A blank channel gets its own visible line rather than being folded into
// Sale — quietly absorbing untagged income would misstate the revenue mix,
// and the whole point of this bucket is that you can see it and go fix it.
const UNASSIGNED_REVENUE = 'Unassigned channel';

function groupTotals(entries, keyOf) {
  const totals = new Map();
  entries.forEach((entry) => {
    const key = keyOf(entry) || UNASSIGNED_REVENUE;
    totals.set(key, (totals.get(key) || 0) + Number(entry.amount_jmd || 0));
  });
  // Largest first: on a P&L the biggest lines are the ones you act on.
  return Array.from(totals, ([label, amount]) => ({ label, amount })).sort(
    (a, b) => b.amount - a.amount
  );
}

export function buildIncomeStatement(income, expenses, period) {
  const yearIncome = filterByPeriod(income, period);
  const yearExpenses = filterByPeriod(expenses, period);

  const cogsEntries = yearExpenses.filter((e) => COGS_CATEGORIES.includes(e.category));
  const opexEntries = yearExpenses.filter((e) => !COGS_CATEGORIES.includes(e.category));

  const revenue = groupTotals(yearIncome, (e) => REVENUE_LINE_LABELS[e.channel] || e.channel);
  const cogs = groupTotals(cogsEntries, (e) => e.category);
  const opex = groupTotals(opexEntries, (e) => e.category);

  const revenueTotal = sumJmd(yearIncome);
  const cogsTotal = sumJmd(cogsEntries);
  const opexTotal = sumJmd(opexEntries);
  const grossProfit = revenueTotal - cogsTotal;
  const netProfit = grossProfit - opexTotal;

  return {
    revenue,
    cogs,
    opex,
    revenueTotal,
    cogsTotal,
    opexTotal,
    grossProfit,
    netProfit,
    // Margins are meaningless (and divide-by-zero) with no revenue booked.
    grossMargin: revenueTotal ? grossProfit / revenueTotal : null,
    netMargin: revenueTotal ? netProfit / revenueTotal : null,
  };
}

// Cumulative profit the business has kept since it started, up to and
// including `asOf`. Always derived, never stored, so it can't disagree with
// the P&L above it. A null `asOf` means "everything recorded".
export function retainedEarningsThrough(income, expenses, asOf) {
  const cutoff = (entry) => !asOf || parseEntryDate(entry.entry_date) <= asOf;
  return sumJmd(income.filter(cutoff)) - sumJmd(expenses.filter(cutoff));
}

export function buildBalanceSheet(items, asOf, retainedEarnings) {
  // Items are "as of" snapshots; the sheet is everything recorded up to the
  // reporting date, most recent value per label winning — so correcting a
  // figure is a matter of recording a newer one, not editing history.
  const eligible = items
    .filter((i) => !asOf || parseEntryDate(i.as_of_date) <= asOf)
    .sort((a, b) => parseEntryDate(a.as_of_date) - parseEntryDate(b.as_of_date));

  const latestByLabel = new Map();
  eligible.forEach((item) => {
    latestByLabel.set(`${item.section}::${item.label}`, item);
  });
  const current = Array.from(latestByLabel.values());

  const inSection = (section) =>
    current
      .filter((i) => i.section === section)
      .map((i) => ({ ...i, amount: Number(i.amount_jmd || 0) }));

  const assets = inSection('Asset');
  const liabilities = inSection('Liability');
  const equityItems = inSection('Equity');

  const total = (rows) => rows.reduce((sum, r) => sum + r.amount, 0);

  const assetsTotal = total(assets);
  const liabilitiesTotal = total(liabilities);
  const equityTotal = total(equityItems) + retainedEarnings;

  return {
    assets,
    liabilities,
    equityItems,
    retainedEarnings,
    assetsTotal,
    liabilitiesTotal,
    equityTotal,
    liabilitiesAndEquity: liabilitiesTotal + equityTotal,
    // A balance sheet that doesn't balance means something is missing or
    // double-counted; surface the gap rather than quietly showing two totals.
    difference: assetsTotal - (liabilitiesTotal + equityTotal),
  };
}

/* ---- CSV row builders -------------------------------------------------
   Each returns an array-of-arrays (first row = header) for lib/csv.js.
   Amounts export as plain numbers, not "J$1,234.56" strings — a formatted
   string lands in a spreadsheet as text and can't be summed. The currency
   is stated in the column header instead. */

const AMOUNT_HEADER = 'Amount (JMD)';

export function expensesCsv(entries) {
  return [
    ['Date', 'Category', 'Description', 'Vendor', 'Currency', 'Amount', 'Exchange rate', AMOUNT_HEADER, 'Notes'],
    ...entries.map((e) => [
      e.entry_date,
      e.category,
      e.description,
      e.vendor,
      e.currency,
      Number(e.amount || 0),
      Number(e.exchange_rate || 1),
      Number(e.amount_jmd || 0),
      e.notes,
    ]),
  ];
}

export function incomeCsv(entries) {
  return [
    ['Date', 'Channel', 'Order number', 'Description', 'Payment method', 'Currency', 'Amount', 'Exchange rate', AMOUNT_HEADER, 'Notes'],
    ...entries.map((e) => [
      e.entry_date,
      e.channel,
      e.order_number,
      e.description,
      e.payment_method,
      e.currency,
      Number(e.amount || 0),
      Number(e.exchange_rate || 1),
      Number(e.amount_jmd || 0),
      e.notes,
    ]),
  ];
}

// Month-by-month rows for whatever span the period covers: 12 rows for a
// full year, 3 for a quarter, 1 for a month — no special-casing per preset.
export function buildMonthlySummary(income, expenses, period) {
  const periodIncome = filterByPeriod(income, period);
  const periodExpenses = filterByPeriod(expenses, period);
  const { start, end } = effectiveRange(period, [...periodIncome, ...periodExpenses]);

  const inMonth = (entries, year, month) =>
    sumJmd(
      entries.filter((e) => {
        const d = parseEntryDate(e.entry_date);
        return d.getFullYear() === year && d.getMonth() === month;
      })
    );

  const rows = monthsBetween(start, end).map(({ year, month, label }) => {
    const incomeTotal = inMonth(periodIncome, year, month);
    const expenseTotal = inMonth(periodExpenses, year, month);
    return { label, incomeTotal, expenseTotal, net: incomeTotal - expenseTotal };
  });

  const incomeTotal = sumJmd(periodIncome);
  const expenseTotal = sumJmd(periodExpenses);

  return { rows, incomeTotal, expenseTotal, net: incomeTotal - expenseTotal };
}

export function monthlySummaryCsv(income, expenses, period, periodLabel) {
  const summary = buildMonthlySummary(income, expenses, period);
  return [
    ['Month', 'Income (JMD)', 'Expenses (JMD)', 'Net (JMD)'],
    ...summary.rows.map((r) => [r.label, r.incomeTotal, r.expenseTotal, r.net]),
    [`${periodLabel} total`, summary.incomeTotal, summary.expenseTotal, summary.net],
  ];
}

export function taxEstimateCsv({ year, incomeTotal, expenseTotal, netChargeable, citRate, citDue, rollingRevenue, gctThreshold }) {
  return [
    ['Line', AMOUNT_HEADER],
    [`Income ${year}`, incomeTotal],
    [`Expenses ${year}`, expenseTotal],
    ['Net chargeable income', netChargeable],
    [`Corporate income tax @ ${(citRate * 100).toFixed(0)}%`, citDue],
    ['Rolling 12-month revenue', rollingRevenue],
    ['GCT registration threshold', gctThreshold],
    ['GCT threshold exceeded', rollingRevenue >= gctThreshold ? 'Yes' : 'No'],
  ];
}

export function incomeStatementCsv(statement, periodLabel) {
  const rows = [
    ['Skin Fairy JA Co. Ltd. — Profit & Loss', `Period: ${periodLabel}`],
    [],
    ['Line', AMOUNT_HEADER],
    ['REVENUE', ''],
    ...statement.revenue.map((r) => [`  ${r.label}`, r.amount]),
    ['Total revenue', statement.revenueTotal],
    [],
    ['COST OF GOODS SOLD', ''],
    ...statement.cogs.map((r) => [`  ${r.label}`, r.amount]),
    ['Total cost of goods sold', statement.cogsTotal],
    [],
    ['Gross profit', statement.grossProfit],
    [],
    ['OPERATING EXPENSES', ''],
    ...statement.opex.map((r) => [`  ${r.label}`, r.amount]),
    ['Total operating expenses', statement.opexTotal],
    [],
    ['Net profit', statement.netProfit],
  ];

  if (statement.grossMargin !== null) {
    rows.push([], ['Gross margin', `${(statement.grossMargin * 100).toFixed(1)}%`]);
    rows.push(['Net margin', `${(statement.netMargin * 100).toFixed(1)}%`]);
  }
  return rows;
}

export function balanceSheetCsv(sheet, asOfLabel) {
  const bySubsection = (rows, subsection) => rows.filter((r) => r.subsection === subsection);

  const sectionRows = (rows) => [
    ...['Current', 'Non-current'].flatMap((sub) => {
      const inSub = bySubsection(rows, sub);
      if (!inSub.length) return [];
      return [[`  ${sub}`, ''], ...inSub.map((r) => [`    ${r.label}`, r.amount])];
    }),
  ];

  return [
    ['Skin Fairy JA Co. Ltd. — Balance Sheet', `As at ${asOfLabel}`],
    [],
    ['Line', AMOUNT_HEADER],
    ['ASSETS', ''],
    ...sectionRows(sheet.assets),
    ['Total assets', sheet.assetsTotal],
    [],
    ['LIABILITIES', ''],
    ...sectionRows(sheet.liabilities),
    ['Total liabilities', sheet.liabilitiesTotal],
    [],
    ['EQUITY', ''],
    ...sheet.equityItems.map((r) => [`  ${r.label}`, r.amount]),
    ['  Retained earnings (computed)', sheet.retainedEarnings],
    ['Total equity', sheet.equityTotal],
    [],
    ['Total liabilities and equity', sheet.liabilitiesAndEquity],
    ['Difference (assets less liabilities and equity)', sheet.difference],
  ];
}

export function formatPercent(value) {
  return value === null ? '—' : `${(value * 100).toFixed(1)}%`;
}

export { formatJMD };
